/**
 * ACT2-1108: "Invite a team member". The Invite member dialog's fields and write, inline. Once an invite is
 * pending, it is listed here, with "Invite another" to send one more, and the step stays open until the
 * person moves on. The done step's subtitle names the pending invites (`usePendingInvites`). An admin can
 * skip the step for the whole workspace instead (`SkipStepControl`). A sibling workspace invites through
 * RudderStack, so there the step links to Settings › Users instead of sending the invite itself.
 */
import dayjs from 'dayjs';
import { UserPlus } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useSiblingModel } from '@/_shared/hooks/useSiblingStatus';
import type { WorkspaceSetupStatus } from '@/_shared/lib/setup-steps';
import { InviteMemberFields } from '@/settings/components/InviteMemberFields';
import { type InviteRole, useInviteMember } from '@/settings/hooks/useInviteMember';

import { usePendingInvites } from '../hooks/usePendingInvites';
import { SETUP_PANEL_TOAST } from '../lib/panelToast';
import { SkipStepControl } from './SkipStepControl';

/** The workspace's pending invites, one row each. Nothing while there are none. */
function PendingInvites(): React.ReactElement | null {
  const invites = usePendingInvites();
  if (invites.length === 0) return null;
  return (
    <ul aria-label="Pending invites" className="flex flex-col gap-2">
      {invites.map((pending) => (
        <li key={pending.id} className="flex items-center gap-3">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm text-foreground" title={pending.email}>
              {pending.email}
            </span>
            <span className="text-xs text-muted-foreground">
              Invite sent · expires {dayjs(pending.expiresAt).format('MMM D')}
            </span>
          </span>
          <Badge variant="info" className="shrink-0 tracking-wider uppercase">
            {pending.role}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

export function InviteStep({ status }: { status: WorkspaceSetupStatus }): React.ReactElement {
  const sibling = useSiblingModel();
  if (sibling.model === 'loading') {
    return (
      <div role="status" aria-label="Loading invite options">
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }
  // Not knowing the model, the email form could be refused (a sibling invites through RudderStack): ask again.
  if (sibling.model === 'unknown') {
    return (
      <div className="flex flex-col gap-2">
        <Callout variant="error">
          <p>Could not check how this workspace invites people.</p>
          <Button
            type="button"
            variant="secondary"
            className="mt-2"
            label="Retry"
            loading={sibling.retrying}
            onClick={sibling.retry}
          />
        </Callout>
        {!status.inviteSent && <SkipStepControl step="invite" status={status} prompt="Working on your own for now?" />}
      </div>
    );
  }
  if (sibling.model === 'sibling') return <SiblingInviteStep status={status} />;
  return <EmailInviteStep status={status} />;
}

/**
 * A sibling workspace (ACT2-1124) invites through RudderStack, with its own checks and a possible password
 * step-up, so `workspace.inviteMember` refuses it. The step sends the person to Settings › Users, which
 * owns that flow.
 */
function SiblingInviteStep({ status }: { status: WorkspaceSetupStatus }): React.ReactElement {
  const { wp } = useWorkspace();
  return (
    <div className="flex flex-col gap-3">
      <PendingInvites />
      <p className="text-sm text-muted-foreground">This workspace invites people through RudderStack.</p>
      <div className="p-0.5">
        <Button asChild variant="secondary">
          <Link to={wp('/settings/users')}>Invite in Settings › Users</Link>
        </Button>
      </div>
      {!status.inviteSent && <SkipStepControl step="invite" status={status} prompt="Working on your own for now?" />}
    </div>
  );
}

function EmailInviteStep({ status }: { status: WorkspaceSetupStatus }): React.ReactElement {
  const idPrefix = useId();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('member');
  const [formOpen, setFormOpen] = useState(false);
  const invite = useInviteMember({
    toastOptions: SETUP_PANEL_TOAST,
    onSent: () => {
      setEmail('');
      setRole('member');
      setFormOpen(false);
    },
  });
  const invites = usePendingInvites();
  const showForm = formOpen || invites.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <PendingInvites />
      {showForm ? (
        <form
          noValidate
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            invite.send(email, role);
          }}
        >
          <InviteMemberFields
            idPrefix={idPrefix}
            email={email}
            onEmailChange={setEmail}
            role={role}
            onRoleChange={setRole}
            disabled={invite.isPending}
          />
          <div className="flex flex-wrap items-center gap-2 p-0.5">
            <Button
              type="submit"
              variant="primary"
              Icon={<UserPlus />}
              loading={invite.isPending}
              label="Send invite"
            />
            <span className="text-xs text-muted-foreground">They get an email. It expires in 7 days.</span>
          </div>
        </form>
      ) : (
        <div className="p-0.5">
          <Button
            type="button"
            variant="secondary"
            Icon={<UserPlus />}
            label="Invite another"
            onClick={() => setFormOpen(true)}
          />
        </div>
      )}
      {!status.inviteSent && <SkipStepControl step="invite" status={status} prompt="Working on your own for now?" />}
    </div>
  );
}
